<div class="dialog-container sai-dialog-shell" *transloco="let t">
    <app-sai-dialog-header
        iconLigature="auto_awesome"
        [title]="t('KPI Modifier')"
        (closed)="close()"
    >
        <div saiDialogHeaderSubtitle class="header-subtitle">
                    {{ t('Modify KPI Formula using AI') }}:
                    <strong>{{ data.metricName }}</strong>
                </div>
    </app-sai-dialog-header>

    <div class="split-body">
        <div class="content content-left">
            <div class="dialog-loading-overlay" *ngIf="isGenerating || isValidating">
                <mat-progress-spinner mode="indeterminate" diameter="52"></mat-progress-spinner>
            </div>

            <div class="inline-alert-wrap mb-4" *ngIf="showAlert">
                <fuse-alert
                    [appearance]="'outline'"
                    [showIcon]="true"
                    [type]="alert.type"
                >
                    {{ alert.message }}
                </fuse-alert>
                <button
                    type="button"
                    mat-icon-button
                    class="inline-alert-close"
                    (click)="closeInlineAlert()"
                    [matTooltip]="t('Close')"
                >
                    <mat-icon>close</mat-icon>
                </button>
            </div>

            <div class="prompt-field-wrapper">
                <mat-form-field class="w-full fuse-mat-dense" appearance="fill" [subscriptSizing]="'dynamic'">
                    <textarea
                        matInput
                        [rows]="5"
                        [spellcheck]="true"
                        [formControl]="promptCtrl"
                        [placeholder]="t('Tell the agent what to update in this KPI Formula')"
                        class="prompt-textarea"
                    ></textarea>
                    <mat-error *ngIf="promptCtrl.hasError('required') && promptCtrl.touched">
                        {{ t('Prompt is required') }}
                    </mat-error>
                </mat-form-field>
                <button
                    type="button"
                    mat-flat-button
                    class="generate-inside-btn"
                    [class.bg-primary]="!promptCtrl.invalid && !isGenerating && !isSaving"
                    [class.bg-invalid]="promptCtrl.invalid || isGenerating || isSaving"
                    [disabled]="promptCtrl.invalid || isGenerating || isSaving"
                    (click)="generate()"
                    [matTooltip]="isGenerating ? t('Generating...') : t('Generate')"
                >
                    <mat-icon class="generate-btn-icon">{{
                        isGenerating ? 'hourglass_top' : 'auto_awesome'
                    }}</mat-icon>
                    <span class="generate-btn-label">{{
                        isGenerating ? t('Generating...') : t('Generate')
                    }}</span>
                </button>
            </div>

            <div *ngIf="oldKpi" class="comparison-card mt-4">
                <div class="comparison-title">
                    {{ hasGeneratedKpi ? t('Current VS Updated Changes') : t('Current VS Updated') }}
                </div>

                <div class="comparison-row">
                    <div class="comparison-label">{{ t('KPI Name') }}</div>
                    <div class="comparison-value" [class.changed]="isFieldChanged('kpi_name')">
                        <div class="old"><span class="value-badge old-badge">{{ t('Current') }}</span><span class="value-text">{{ oldKpi.kpi_name }}</span></div>
                        <div class="new"><span class="value-badge new-badge">{{ t('Updated') }}</span><span class="value-text">{{ generatedKpi?.kpi_name || '-' }}</span></div>
                    </div>
                </div>

                <div class="comparison-row">
                    <div class="comparison-label">{{ t('Calculation') }}</div>
                    <div class="comparison-value" [class.changed]="isFieldChanged('calculation')">
                        <div class="old"><span class="value-badge old-badge">{{ t('Current') }}</span><span class="value-text">{{ oldKpi.calculation }}</span></div>
                        <div class="new"><span class="value-badge new-badge">{{ t('Updated') }}</span><span class="value-text">{{ generatedKpi?.calculation || '-' }}</span></div>
                    </div>
                </div>

                <div class="comparison-row">
                    <div class="comparison-label">{{ t('Description') }}</div>
                    <div class="comparison-value" [class.changed]="isFieldChanged('description')">
                        <div class="old"><span class="value-badge old-badge">{{ t('Current') }}</span><span class="value-text">{{ oldKpi.description }}</span></div>
                        <div class="new"><span class="value-badge new-badge">{{ t('Updated') }}</span><span class="value-text">{{ generatedKpi?.description || '-' }}</span></div>
                    </div>
                </div>

                <div class="comparison-row">
                    <div class="comparison-label">{{ t('Columns Used') }}</div>
                    <div class="comparison-value" [class.changed]="isFieldChanged('columns_used')">
                        <div class="old"><span class="value-badge old-badge">{{ t('Current') }}</span><span class="value-text">{{ (oldKpi.columns_used || []).join(', ') || '-' }}</span></div>
                        <div class="new"><span class="value-badge new-badge">{{ t('Updated') }}</span><span class="value-text">{{ (generatedKpi?.columns_used || []).join(', ') || '-' }}</span></div>
                    </div>
                </div>
            </div>

            <div class="comparison-card mt-4">
                <div class="validation-header-row">
                    <div class="comparison-title mb-0">{{ t('Validation Preview') }}</div>
                    <div class="validation-actions">
                        <button
                            *ngIf="hasGeneratedKpi"
                            type="button"
                            mat-flat-button
                            class="me-2"
                            [class.bg-primary]="!isValidating && !isSaving && !isGenerating"
                            [class.bg-invalid]="isValidating || isSaving || isGenerating"
                            [disabled]="isValidating || isSaving || isGenerating"
                            (click)="validateUpdatedKpiData()"
                        >
                            {{ isValidating ? t('Validating...') : t('Validate Updated Values') }}
                        </button>
                        <button
                            type="button"
                            mat-flat-button
                            [class.bg-primary]="!isValidating && !isSaving && !isGenerating"
                            [class.bg-invalid]="isValidating || isSaving || isGenerating"
                            [disabled]="isValidating || isSaving || isGenerating"
                            (click)="validateKpi()"
                        >
                            {{ isValidating ? t('Validating...') : t('Validate Current Values') }}
                        </button>
                    </div>
                </div>

                <ng-container *ngIf="validationPreview; else preValidateInfo">
                    <div class="mb-3 validation-section">
                        <div class="sql-header-row">
                            <div class="comparison-label mb-1">{{ t('SQL Query') }}</div>
                            <button
                                type="button"
                                mat-icon-button
                                class="copy-sql-btn"
                                [disabled]="!validationPreview.sql"
                                [cdkCopyToClipboard]="validationPreview.sql"
                                (cdkCopyToClipboardCopied)="onSqlCopied($event)"
                                [matTooltip]="t('Copy query')"
                            >
                                <mat-icon class="icon-size-4">content_copy</mat-icon>
                            </button>
                        </div>
                        <pre class="sql-block bg-gray-100 p-4 rounded-md text-sm">
                            <code class="language-sql" [innerHTML]="highlightedValidationSql"></code>
                        </pre>
                    </div>

                    <div class="validation-section">
                        <div class="comparison-label mb-1">{{ t('Result Rows') }}</div>
                        <div class="validation-table-wrap" *ngIf="validationPreview.rows.length; else noRows">
                            <table class="validation-table">
                                <thead>
                                    <tr>
                                        <th *ngFor="let col of (validationPreview.rows[0] | keyvalue)">
                                            {{ col.key }}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr *ngFor="let row of validationPreview.rows">
                                    <td *ngFor="let col of (row | keyvalue)">
                                        {{ formatPreviewCellValue(col.value) }}
                                    </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <ng-template #noRows>
                            <div class="text-xs text-gray-500">{{ t('No rows returned.') }}</div>
                        </ng-template>
                    </div>
                </ng-container>
                <ng-template #preValidateInfo>
                    <div class="text-xs text-gray-500">
                        {{ t('Click Validate to preview SQL query and result rows.') }}
                    </div>
                </ng-template>
            </div>

            <div class="flex justify-end gap-2 mt-6 dialog-actions">
                <button
                    type="button"
                    mat-button
                    (click)="close()"
                    [disabled]="isGenerating || isSaving || isValidating"
                >
                    {{ t('Cancel') }}
                </button>
                <button
                    type="button"
                    mat-flat-button
                    [class.bg-primary]="hasGeneratedKpi && !isSaving && !isGenerating && !isValidating"
                    [class.bg-invalid]="!hasGeneratedKpi || isSaving || isGenerating || isValidating"
                    [disabled]="!hasGeneratedKpi || isSaving || isGenerating || isValidating"
                    (click)="saveGeneratedKpi()"
                >
                    {{ isSaving ? t('Saving...') : t('Save') }}
                </button>
            </div>
        </div>

        <aside class="content-right" *ngIf="previewHeaders.length">
            <div class="table-preview-section">
                <div class="flex justify-between items-center flex-shrink-0 mb-2">
                    <label class="text-sm section-title">{{
                        t('Table Preview')
                    }}</label>
                </div>

                <div
                    class="table-container table-preview formula-dialog-table-preview"
                >
                    <table class="font-sans text-xs">
                        <thead class="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                            <tr>
                                <th
                                    class="px-4 py-3 text-left text-sm font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider border-r dark:border-gray-600"
                                    *ngFor="
                                        let h of previewHeaders;
                                        let i = index;
                                        trackBy: trackByHeaderIndex
                                    "
                                >
                                    <div class="header-cell">
                                        <span class="header-text">{{ h }}</span>
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                *ngFor="
                                    let row of formattedPreviewRows;
                                    let j = index;
                                    trackBy: trackByRowIndex
                                "
                            >
                                <td
                                    *ngFor="let cell of row; trackBy: trackByHeaderIndex"
                                    class="copy-cell hover:bg-gray-200"
                                >
                                    {{ cell }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </aside>
    </div>
</div>
